export const normalizeSeatId = (seatId) => String(seatId ?? '').trim()

export const getSeatMapValue = (seatMap, seatId) => {
  if (!seatMap) return undefined
  const normalizedSeatId = normalizeSeatId(seatId)
  return seatMap.get(seatId) || seatMap.get(normalizedSeatId)
}

export const countSelectedSeats = (selectedSeatIds = {}) => {
  let total = 0
  if (selectedSeatIds?.seats) {
    total += Object.keys(selectedSeatIds.seats).length
  }
  if (selectedSeatIds?.gaSeats) {
    Object.values(selectedSeatIds.gaSeats).forEach((seatIds) => {
      total += Array.isArray(seatIds) ? seatIds.length : 0
    })
  }
  return total
}

export const countSelectedSeatsExcludingGaSection = (
  selectedSeatIds = {},
  sectionId
) => {
  let total = 0
  if (selectedSeatIds?.seats) {
    total += Object.keys(selectedSeatIds.seats).length
  }
  if (selectedSeatIds?.gaSeats) {
    Object.entries(selectedSeatIds.gaSeats).forEach(([id, seatIds]) => {
      if (String(id) === String(sectionId)) return
      total += Array.isArray(seatIds) ? seatIds.length : 0
    })
  }
  return total
}

export const getAvailableGaPackageSeatIds = (
  eventPackage,
  sectionId,
  soldMap,
  reservedMap
) => {
  const normalizedSectionId = normalizeSeatId(sectionId)
  if (!normalizedSectionId) return []

  const seatIds = []
  for (const ticket of eventPackage?.package_tickets || []) {
    const isGa = Boolean(ticket?.GA ?? ticket?.ga)
    const ticketSectionId = normalizeSeatId(
      ticket?.sectionId ?? ticket?.section_id
    )
    const seatId = normalizeSeatId(ticket?.seatId ?? ticket?.seat_id)
    if (
      !isGa ||
      !seatId ||
      ticketSectionId !== normalizedSectionId ||
      ticket?.on_sale_status !== 'available'
    ) {
      continue
    }
    if (
      getSeatMapValue(soldMap, seatId) ||
      getSeatMapValue(reservedMap, seatId)
    ) {
      continue
    }
    seatIds.push(seatId)
  }
  return [...new Set(seatIds)]
}

export const sectionHasPackageGaInventory = (eventPackage, sectionId) => {
  const normalizedSectionId = normalizeSeatId(sectionId)
  if (!normalizedSectionId) return false
  return (eventPackage?.package_tickets || []).some((ticket) => {
    const isGa = Boolean(ticket?.GA ?? ticket?.ga)
    const ticketSectionId = normalizeSeatId(
      ticket?.sectionId ?? ticket?.section_id
    )
    return isGa && ticketSectionId === normalizedSectionId
  })
}

export const getHoldLabel = (holdInfo) => {
  if (!holdInfo || holdInfo === true) return null
  if (holdInfo.categoryName && holdInfo.holdName) {
    return `${holdInfo.holdName} (${holdInfo.categoryName})`
  }
  return holdInfo.holdName || holdInfo.categoryName || null
}

export const formatHoldNames = (holdLabels) => {
  const uniqueLabels = [...new Set(holdLabels.filter(Boolean))]
  if (uniqueLabels.length === 0) return null
  if (uniqueLabels.length === 1) return uniqueLabels[0]
  if (uniqueLabels.length === 2) {
    return `${uniqueLabels[0]} and ${uniqueLabels[1]}`
  }
  return `${uniqueLabels.slice(0, -1).join(', ')}, and ${uniqueLabels.at(-1)}`
}

export const buildHoldMap = (heldSeatIds, heldSeatInfoById = {}) => {
  const ids = Array.isArray(heldSeatIds)
    ? heldSeatIds
    : Array.from(heldSeatIds || [])

  return new Map(
    ids
      .map((id) => normalizeSeatId(id))
      .filter(Boolean)
      .map((id) => [id, heldSeatInfoById?.[id] || true])
  )
}

/**
 * Split a click into seats that change immediately and held seats that still
 * need confirmation. Deselects happen first so they free capacity. Open seats
 * fill before holds, so cancelling a hold prompt does not skip an open seat.
 */
export const partitionSeatClicks = (
  seatIds = [],
  { isDisabled, isSelected, getHoldInfo, selectedCount, maxSelectable }
) => {
  const actionable = seatIds.filter((id) => id && !isDisabled(id))
  const toToggle = actionable.filter((id) => isSelected(id))
  let count = Math.max(0, selectedCount - toToggle.length)

  const heldCandidates = []
  for (const id of actionable) {
    if (isSelected(id)) continue
    const holdInfo = getHoldInfo(id)
    if (holdInfo) {
      heldCandidates.push({ id, holdInfo })
      continue
    }
    if (count >= maxSelectable) continue
    toToggle.push(id)
    count += 1
  }

  const heldToAdd = []
  const holdLabels = []
  for (const candidate of heldCandidates) {
    if (count >= maxSelectable) break
    heldToAdd.push(candidate.id)
    holdLabels.push(getHoldLabel(candidate.holdInfo))
    count += 1
  }

  return { toToggle, heldToAdd, holdLabels }
}

export const trimSelectedSeatsToCount = (selectedSeatIds = {}, maxCount) => {
  const currentSelectedCount = countSelectedSeats(selectedSeatIds)
  if (currentSelectedCount <= maxCount) return selectedSeatIds

  let remaining = Math.max(maxCount, 0)
  const next = {}

  const seats = { ...(selectedSeatIds?.seats || {}) }
  const limitedSeatIds = Object.keys(seats).slice(0, remaining)
  if (limitedSeatIds.length > 0) {
    const nextSeats = {}
    limitedSeatIds.forEach((id) => {
      nextSeats[id] = id
    })
    next.seats = nextSeats
    remaining -= limitedSeatIds.length
  }

  if (remaining > 0 && selectedSeatIds?.gaSeats) {
    const nextGaSeats = {}
    for (const [sectionId, seatIds] of Object.entries(
      selectedSeatIds.gaSeats
    )) {
      if (remaining <= 0) break
      const limited = (Array.isArray(seatIds) ? seatIds : []).slice(
        0,
        remaining
      )
      if (limited.length > 0) {
        nextGaSeats[sectionId] = limited
        remaining -= limited.length
      }
    }
    if (Object.keys(nextGaSeats).length > 0) next.gaSeats = nextGaSeats
  }

  return next
}
